import { Component, Inject, Input, Optional } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReportingService } from 'src/app/services/reporting/reporting.service';
import { IQuestionScore } from 'src/app/interfaces/iquestion-score';
import { ResponseTimeService } from 'src/app/services/general/response-time.service';
import { IQuestionFactResponse } from 'src/app/interfaces/iquestion-fact-response';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IFact } from 'src/app/interfaces/ifact';
import { ICallOverview } from 'src/app/interfaces/icall-overview';
import { CallGuidedDetailsSidePanelComponent } from '../call-guided-details-side-panel/call-guided-details-side-panel.component';
import { IObjectiveDetails } from 'src/app/interfaces/iobjective-details';
import { CallConversationalDetailsSidePanelComponent } from '../call-conversational-details-side-panel/call-conversational-details-side-panel.component';
import { AppConstants } from 'src/app/app-constants';
import { IMessageAdherenceDetailsResponse } from 'src/app/interfaces/imessage-adherence-details-response';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-question-fact',
  templateUrl: './question-fact.component.html',
  standalone: false,
  styleUrls: ['./question-fact.component.scss'],
})
export class QuestionFactComponent {
  @Input() question: IQuestionScore | undefined;
  @Input() questionNumber: number = 0;
  @Input() isModal: boolean = false;
  @Input() accountUuid: string = '';
  @Input() call: ICallOverview | undefined;
  @Input() moduleName: String = '';
  @Input() moduleUuid: string = '';

  progress: number = 3.6;
  totalSegments: number = 5;
  segments: any[] = new Array(this.totalSegments);
  filledSegments: number = 0;
  remainingPercentage: number = 0;
  remainingFraction: number = 0;
  isOverflowing: boolean = false;
  questionResponse: IQuestionFactResponse | undefined;
  isLoading: boolean = true;
  anyFactNull: boolean = false;
  questionUuid: string = '';
  type: string = '';
  callName: string = '';
  isSidePaneVisible: boolean = false;
  filteredFact: IFact | undefined;
  chevronUp: string = 'assets/icons/arrow/chevron-up-gray.svg';
  chevronDown: string = 'assets/icons/arrow/chevron-down-gray.svg';
  titleCharLimit: number = 60;
  totalScore: number = 5;
  objectiveResult: IObjectiveDetails[] = [];
  messageAdherenceDetailsResponse: IMessageAdherenceDetailsResponse[] = [];
  translatedType: string = '';

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    @Optional() public dialogRef: MatDialogRef<QuestionFactComponent>,
    private reportingService: ReportingService,
    private responseTimeService: ResponseTimeService,
    private dialog: MatDialog,
    private amplitudeService: AmplitudeService,
    private translocoService: TranslocoService
  ) { }

  ngOnInit() {
    if (this.data && this.data.isModal) {
      this.accountUuid = this.data.accountUuid;
      this.isModal = this.data.isModal;
      this.questionUuid = this.data.questionUuid;
      this.type = this.data.type;
      this.callName = this.data.callName;
      this.call = this.data.call;
      this.moduleName = this.data.moduleName;
      this.moduleUuid = this.data.moduleUuid;
      this.titleCharLimit = 90;
      this.translatedType = this.data.translatedType
    }
    if (this.type === 'Highest Scoring Objective' || this.type === 'Lowest Scoring Objective') {
      this.getObjectiveDetails();
    } else if(this.type === 'Most Articulated Message' || this.type === 'Least Articulated Message') {
      this.fetchMessageAdherenceDetails();
    }
     else {
      this.getCallQuestionFactDetail();
    }
  }

  calculateProgress(progress: number, total: number): any {
    if (progress < 0) progress = 0;
    if (progress > total) progress = total;
    let filled = Math.floor(progress);
    return { filled: filled, partial: progress - filled };
  }

  getCallQuestionFactDetail(): void {
    this.responseTimeService
      .logResponseTime(
        this.reportingService.getCallQuestionFactDetail(
          this.accountUuid,
          this.call?.callUuid || '',
          this.question?.questionUuid || this.questionUuid || '',
          this.moduleUuid
        ),
        'getCallQuestionFactDetail'
      )
      .subscribe((response: IQuestionFactResponse) => {
        this.questionResponse = response;
        if (this.isModal && this.data && this.data.factNumber != null) {
          this.filterFact();
        } else {
          this.anyFactNull = this.isAnyFactNameNull(response);
        }
        this.isLoading = false;
      });
  }

  isAnyFactNameNull(questionData: IQuestionFactResponse | IFact): boolean {
    if (
      'factDetails' in questionData &&
      Array.isArray(questionData.factDetails)
    ) {
      return questionData.factDetails.some(
        (fact: any) => fact.factName === null
      );
    } else if ('factName' in questionData) {
      return questionData.factName === null;
    }
    return false;
  }

  close(): void {
    this.isModal = false;
    this.dialogRef.close();
  }

  openSidePane(payload?: IObjectiveDetails | IMessageAdherenceDetailsResponse): void {
    console.log("///////////", payload)

    // Sending the Event of Clicking on Call Details to Amplitude
    this.amplitudeService.trackEvent('modulereport_click_calldetails', {
      'call_name': payload ? payload?.callName : this.call?.callName,
    });

    this.close();
    if ((this.call?.callType || payload?.callType) === AppConstants.CALL_TYPE_CONVERSATIONAL) {
      this.dialog.open(CallConversationalDetailsSidePanelComponent, {
        hasBackdrop: true,
        disableClose: true,
        position: { right: '0', top: '0' },
        width: '720px',
        data: {
          accountUuid: this.accountUuid,
          moduleName: this.moduleName,
          moduleUuid: this.moduleUuid,
          call: payload ? payload : this.call,
        },
      });

    } else if ((this.call?.callType || payload?.callType) === AppConstants.CALL_TYPE_GUIDED) {
      this.dialog.open(CallGuidedDetailsSidePanelComponent, {
        hasBackdrop: true,
        disableClose: true,
        position: { right: '0', top: '0' },
        data: {
          accountUuid: this.accountUuid,
          moduleName: this.moduleName,
          moduleUuid: this.moduleUuid,
          call: payload ? payload : this.call,
        },
      });
    }
  }

  filterFact(): void {
    if (this.questionResponse && this.questionResponse.factDetails) {
      this.filteredFact = this.questionResponse.factDetails.find(
        (fact) => fact.factName === this.data.name
      );
      if (this.filteredFact) {
        this.anyFactNull = this.isAnyFactNameNull(this.filteredFact);
      }
    }
  }

  toggleExpand(event: Event, fullName: string) {
    const clickedElement = event.currentTarget as HTMLElement;
    const titleElement = clickedElement.querySelector('.title') as HTMLElement | null;
    const showAllContainer = clickedElement.querySelector('.show-all') as HTMLElement | null;

    if (showAllContainer && titleElement) {
      const span = showAllContainer.querySelector('span');
      const img = showAllContainer.querySelector('img');

      if (span && img) {
        const isExpanded = span.textContent === this.translocoService.translate('SHOW_ALL', {}, 'reporting');
        span.textContent = isExpanded ? this.translocoService.translate('CLOSE_ALT', {}, 'reporting') : this.translocoService.translate('SHOW_ALL', {}, 'reporting');
        img.src = isExpanded ? this.chevronUp : this.chevronDown;
        titleElement.textContent = isExpanded ? fullName : fullName.slice(0, this.titleCharLimit) + '...';
      }
    }
  }

  convertToNumber(value: string): number {
    return Number(value) || 0;
  }

  getObjectiveDetails(): void {
    this.responseTimeService
      .logResponseTime(
        this.reportingService.getObjectiveDetails(
          this.accountUuid,
          this.moduleUuid,
          this.data.name
        ),
        'getObjectiveDetails'
      )
      .subscribe((response: IObjectiveDetails[]) => {
        this.objectiveResult = response;
        this.isLoading = false;
      });
  }

  fetchMessageAdherenceDetails(): void {
    this.responseTimeService
      .logResponseTime(
        this.reportingService.fetchMessageAdherenceDetails(
          this.accountUuid,
          this.moduleUuid,
          this.data.name
        ),
        'fetchMessageAdherenceDetails'
      )
      .subscribe((response: IMessageAdherenceDetailsResponse[]) => {
        const details = response.map(item => {
          // Convert articulationProgress to Map if it's not already a Map
          return {
            ...item,
            articulationProgress: new Map(Object.entries(item.articulationProgress || {})),
          };
        });
      
        this.messageAdherenceDetailsResponse = details;
        this.isLoading = false;
      });      
  }
  
}
